import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usage } from '../services/api';
import type { UsageHistoryItem, UsageHistoryTotals, UsageDetailResponse, Pagination } from '../types/api';
import {
  USAGE_TYPE_FILTER_OPTIONS,
  formatUsageFunctionalityLabel,
  getUsageFunctionalityBadgeStyle,
  resolveUsageFunctionalityKey,
} from '../utils/usageFunctionality';
import DatePicker from './ui/DatePicker';
import { formatOptimizationDatasetDisplay } from '../utils/optimizationDatasetName';
import { getDefaultTrainingDateRangeYmd } from '../utils/defaultTrainingDateRange';
import {
  formatUsageTimelineCompleted,
  formatUsageTimelineCreated,
  mergeUsageDetailWithListItem,
  resolveUsageDurationMs,
  formatUsageDurationMs,
} from '../utils/usageDateTime';

const PAGE_SIZE = 20;

const DEFAULT_USAGE_FILTER_DATES = getDefaultTrainingDateRangeYmd();

const UsageTransactionsPage: React.FC = () => {
  const [items, setItems] = useState<UsageHistoryItem[]>([]);
  const [totals, setTotals] = useState<UsageHistoryTotals | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [functionality, setFunctionality] = useState('');
  const [dateFrom, setDateFrom] = useState(DEFAULT_USAGE_FILTER_DATES.from);
  const [dateTo, setDateTo] = useState(DEFAULT_USAGE_FILTER_DATES.to);

  const [detailItem, setDetailItem] = useState<UsageHistoryItem | null>(null);
  const [detail, setDetail] = useState<UsageDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleDateFromChange = (nextFrom: string) => {
    setDateFrom(nextFrom);
    if (nextFrom && dateTo && nextFrom > dateTo) setDateTo(nextFrom);
  };

  const handleDateToChange = (nextTo: string) => {
    setDateTo(nextTo);
    if (nextTo && dateFrom && nextTo < dateFrom) setDateFrom(nextTo);
  };

  const load = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const from = dateFrom;
      const to = dateTo;
      if (from && to && from > to) {
        setError('Invalid date range: From must be before or equal to To.');
        setIsLoading(false);
        return;
      }
      const params: any = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (functionality) params.functionality = functionality;
      if (from) params.from = new Date(from).toISOString();
      if (to) {
        const toDate = new Date(to);
        // If the user selects the same day for both bounds, some backends treat it
        // as an invalid (empty) range unless `to` is moved to the end of the day.
        if (from && from === to) {
          toDate.setUTCHours(23, 59, 59, 999);
          params.to = toDate.toISOString();
        } else {
          params.to = toDate.toISOString();
        }
      }

      const response = await usage.getHistory(params);
      if (response.success && response.data) {
        setItems(response.data.items || []);
        setTotals(response.data.totals || null);
        setPagination(response.data.pagination || null);
        setCurrentPage(page);
      } else {
        setError('Failed to load usage history');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load usage');
    } finally {
      setIsLoading(false);
    }
  }, [functionality, dateFrom, dateTo]);

  useEffect(() => { void load(1); }, [load]);

  const openDetail = async (item: UsageHistoryItem) => {
    if (!item.entity_id) return;
    setDetailItem(item);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await usage.getDetail(
        resolveUsageFunctionalityKey(item.functionality, item.entity_id),
        item.entity_id
      );
      setDetail(
        resp?.data ? mergeUsageDetailWithListItem(resp.data, item) : null
      );
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetailItem(null); setDetail(null); };
  const totalPages = pagination ? Math.ceil(pagination.total / PAGE_SIZE) : 1;
  const hasFilters = !!(functionality || dateFrom || dateTo);

  return (
    <div className="usage-page" id="profile-usage-section">
      <style>{usageStyles}</style>

      <div className="usage-header">
        <h2 className="usage-title">
          <i className="fas fa-chart-bar" style={{ color: '#0099FA', marginRight: 8 }}></i>
          Usage History
        </h2>
        <button onClick={() => load(currentPage)} disabled={isLoading} className="usage-refresh-btn">
          <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i> Refresh
        </button>
      </div>

      {/* Totals */}
      {totals && (
        <div className="usage-totals-grid">
          <div className="usage-total-card usage-total-card--primary">
            <div className="usage-total-label">Total Tokens</div>
            <div className="usage-total-value">{fmtNum(totals.total_tokens)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Prompt</div>
            <div className="usage-total-value">{fmtNum(totals.prompt_tokens)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Completion</div>
            <div className="usage-total-value">{fmtNum(totals.completion_tokens)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Cached</div>
            <div className="usage-total-value">{fmtNum(totals.cached_tokens)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Entities</div>
            <div className="usage-total-value">{fmtNum(totals.entities_count)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Records</div>
            <div className="usage-total-value">{fmtNum(totals.records_count)}</div>
          </div>
          <div className="usage-total-card">
            <div className="usage-total-label">Total Cost</div>
            <div className="usage-total-value">{fmtCost(totals.total_cost)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="usage-filters">
        <div className="usage-filter-field">
          <label>Type</label>
          <select value={functionality} onChange={e => setFunctionality(e.target.value)}>
            {USAGE_TYPE_FILTER_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="usage-filter-field">
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={handleDateFromChange}
            placeholder="From date"
            max={dateTo || undefined}
          />
        </div>
        <div className="usage-filter-field">
          <DatePicker
            label="To"
            value={dateTo}
            onChange={handleDateToChange}
            placeholder="To date"
            min={dateFrom || undefined}
          />
        </div>
        {hasFilters && (
          <button className="usage-clear-btn" onClick={() => { setFunctionality(''); setDateFrom(''); setDateTo(''); }}>
            <i className="fas fa-times"></i> Clear
          </button>
        )}
      </div>

      {error && <div className="usage-error">{error}</div>}

      {isLoading && items.length === 0 ? (
        <div className="usage-loading"><div className="spinner"></div><p>Loading usage...</p></div>
      ) : items.length === 0 ? (
        <div className="usage-empty">
          <i className="fas fa-receipt"></i>
          <h3>No usage data yet</h3>
          <p>When you run optimizations, fine tuning, or experiments, usage will appear here.</p>
        </div>
      ) : (
        <>
          {pagination && (
            <div className="usage-count">Showing {items.length} of {fmtNum(pagination.total)} records</div>
          )}

          {/* Desktop table */}
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Prompt</th>
                  <th style={{ textAlign: 'right' }}>Completion</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Cached</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const clickable = !!item.entity_id;
                  return (
                    <tr
                      key={`${item.entity_id || idx}-${item.created_at}`}
                      className={clickable ? 'usage-row-clickable' : ''}
                      onClick={() => clickable && openDetail(item)}
                    >
                      <td>{formatUsageTimelineCreated(item)}</td>
                      <td>
                        <FunctionalityBadge
                          value={resolveUsageFunctionalityKey(item.functionality, item.entity_id)}
                        />
                      </td>
                      <td><StatusBadge value={item.status} /></td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(item.prompt_tokens)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(item.completion_tokens)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(item.total_tokens)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(item.cached_tokens)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtCost(item.total_cost)}</td>
                      <td style={{ width: 32, textAlign: 'center' }}>
                        {clickable && <i className="fas fa-chevron-right usage-row-arrow"></i>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="usage-cards">
            {items.map((item, idx) => {
              const clickable = !!item.entity_id;
              return (
                <div
                  key={`m-${item.entity_id || idx}-${item.created_at}`}
                  className={`usage-card ${clickable ? 'usage-card--clickable' : ''}`}
                  onClick={() => clickable && openDetail(item)}
                >
                  <div className="usage-card-top">
                    <FunctionalityBadge
                      value={resolveUsageFunctionalityKey(item.functionality, item.entity_id)}
                    />
                    <StatusBadge value={item.status} />
                  </div>
                  <div className="usage-card-date">{formatUsageTimelineCreated(item)}</div>
                  <div className="usage-card-tokens">
                    <div className="usage-card-token">
                      <span className="usage-card-token-label">Total</span>
                      <span className="usage-card-token-value usage-card-token-value--bold">{fmtNum(item.total_tokens)}</span>
                    </div>
                    <div className="usage-card-token">
                      <span className="usage-card-token-label">Prompt</span>
                      <span className="usage-card-token-value">{fmtNum(item.prompt_tokens)}</span>
                    </div>
                    <div className="usage-card-token">
                      <span className="usage-card-token-label">Compl.</span>
                      <span className="usage-card-token-value">{fmtNum(item.completion_tokens)}</span>
                    </div>
                    {item.cached_tokens > 0 && (
                      <div className="usage-card-token">
                        <span className="usage-card-token-label">Cached</span>
                        <span className="usage-card-token-value">{fmtNum(item.cached_tokens)}</span>
                      </div>
                    )}
                    <div className="usage-card-token">
                      <span className="usage-card-token-label">Cost</span>
                      <span className="usage-card-token-value">{fmtCost(item.total_cost)}</span>
                    </div>
                  </div>
                  {clickable && (
                    <div className="usage-card-hint">
                      Tap to view details <i className="fas fa-arrow-right" style={{ fontSize: 10 }}></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="usage-pagination">
              <button disabled={currentPage <= 1 || isLoading} onClick={() => load(currentPage - 1)} className="usage-page-btn">
                <i className="fas fa-chevron-left"></i> Prev
              </button>
              <span className="usage-page-info">Page {currentPage} / {totalPages}</span>
              <button disabled={currentPage >= totalPages || isLoading} onClick={() => load(currentPage + 1)} className="usage-page-btn">
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal — rendered in document.body so it always stays above header */}
      {detailItem && createPortal(
        <div className="usage-modal-overlay">
          <div className="usage-modal">
            <div className="usage-modal-header">
              <h3>
                <FunctionalityBadge
                  value={resolveUsageFunctionalityKey(detailItem.functionality, detailItem.entity_id)}
                />
                <span style={{ marginLeft: 8 }}>Usage Detail</span>
              </h3>
              <button onClick={closeDetail} className="usage-modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            {detailLoading ? (
              <div className="usage-modal-loading">Loading...</div>
            ) : !detail ? (
              <div className="usage-modal-loading" style={{ color: '#7F9AB3' }}>Could not load details.</div>
            ) : (
              <>
                <div className="usage-detail-grid">
                  <DetailField label="Status"><StatusBadge value={detail.status} /></DetailField>
                  <DetailField label="Created">
                    {formatUsageTimelineCreated(detail, detailItem)}
                  </DetailField>
                  <DetailField label="Completed">
                    {formatUsageTimelineCompleted(detail, detailItem)}
                  </DetailField>
                  {(() => {
                    const durationMs = resolveUsageDurationMs(detail, detailItem);
                    return durationMs != null ? (
                      <DetailField label="Duration">{formatUsageDurationMs(durationMs)}</DetailField>
                    ) : null;
                  })()}
                  {detail.entity_id && <DetailField label="Entity ID"><code className="usage-code">{detail.entity_id}</code></DetailField>}
                  {detail.evaluation_run_id && (
                    <DetailField label="Evaluation Run">
                      <code className="usage-code">{detail.evaluation_run_id}</code>
                    </DetailField>
                  )}
                  {detail.optimization_run_id && <DetailField label="Run ID"><code className="usage-code">{detail.optimization_run_id}</code></DetailField>}
                  {detail.functionality === 'optimization' &&
                    formatOptimizationDatasetDisplay(detail.dataset_name) && (
                      <DetailField label="Dataset">
                        {formatOptimizationDatasetDisplay(detail.dataset_name)}
                      </DetailField>
                    )}
                  {detail.functionality === 'optimization' && detail.iterations != null && <DetailField label="Iterations">{detail.iterations}</DetailField>}
                  {detail.training_job_id && <DetailField label="Training Job"><code className="usage-code">{detail.training_job_id}</code></DetailField>}
                  {detail.experiment_id && <DetailField label="Experiment"><code className="usage-code">{detail.experiment_id}</code></DetailField>}
                  {detail.chat_id && <DetailField label="Chat"><code className="usage-code">{detail.chat_id}</code></DetailField>}
                </div>

                <div className="usage-totals-grid" style={{ marginBottom: 16 }}>
                  <div className="usage-total-card usage-total-card--primary">
                    <div className="usage-total-label">Total</div>
                    <div className="usage-total-value">{fmtNum(detail.usage.total_tokens)}</div>
                  </div>
                  <div className="usage-total-card"><div className="usage-total-label">Prompt</div><div className="usage-total-value">{fmtNum(detail.usage.prompt_tokens)}</div></div>
                  <div className="usage-total-card"><div className="usage-total-label">Completion</div><div className="usage-total-value">{fmtNum(detail.usage.completion_tokens)}</div></div>
                  <div className="usage-total-card"><div className="usage-total-label">Cached</div><div className="usage-total-value">{fmtNum(detail.usage.cached_tokens)}</div></div>
                  <div className="usage-total-card"><div className="usage-total-label">Records</div><div className="usage-total-value">{fmtNum(detail.usage.records_count)}</div></div>
                  <div className="usage-total-card"><div className="usage-total-label">Total Cost</div><div className="usage-total-value">{fmtCost(detail.usage.total_cost)}</div></div>
                </div>

                {detail.usage.by_operation && detail.usage.by_operation.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: 13, fontWeight: 600, color: '#4A6683', marginBottom: 8 }}>Breakdown by Operation</h4>
                    {/* Desktop table */}
                    <div className="usage-table-wrap">
                      <table className="usage-table usage-table--compact">
                        <thead>
                          <tr>
                            <th>Operation</th>
                            <th style={{ textAlign: 'right' }}>Prompt</th>
                            <th style={{ textAlign: 'right' }}>Completion</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Cached</th>
                            <th style={{ textAlign: 'right' }}>Cost</th>
                            <th style={{ textAlign: 'right' }}>Records</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.usage.by_operation.map((op, i) => (
                            <tr key={i}>
                              <td>{op.operation}</td>
                              <td style={{ textAlign: 'right' }}>{fmtNum(op.prompt_tokens)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtNum(op.completion_tokens)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(op.total_tokens)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtNum(op.cached_tokens)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtCost(op.total_cost)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtNum(op.records_count)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile operation cards */}
                    <div className="usage-cards">
                      {detail.usage.by_operation.map((op, i) => (
                        <div key={i} className="usage-card" style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{op.operation}</div>
                          <div className="usage-card-tokens">
                            <div className="usage-card-token"><span className="usage-card-token-label">Total</span><span className="usage-card-token-value usage-card-token-value--bold">{fmtNum(op.total_tokens)}</span></div>
                            <div className="usage-card-token"><span className="usage-card-token-label">Prompt</span><span className="usage-card-token-value">{fmtNum(op.prompt_tokens)}</span></div>
                            <div className="usage-card-token"><span className="usage-card-token-label">Compl.</span><span className="usage-card-token-value">{fmtNum(op.completion_tokens)}</span></div>
                            <div className="usage-card-token"><span className="usage-card-token-label">Cost</span><span className="usage-card-token-value">{fmtCost(op.total_cost)}</span></div>
                            <div className="usage-card-token"><span className="usage-card-token-label">Records</span><span className="usage-card-token-value">{fmtNum(op.records_count)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// --- Sub-components ---

const fmtNum = (v: number | null | undefined) => (v ?? 0).toLocaleString();
const fmtCost = (v: number | null | undefined) => `$${(v ?? 0).toFixed(6)}`;

const FunctionalityBadge: React.FC<{ value: string }> = ({ value }) => {
  const c = getUsageFunctionalityBadgeStyle(value);
  const label = formatUsageFunctionalityLabel(value);
  return (
    <span className="usage-func-badge" style={{ background: c.bg, color: c.color }}>
      <i className={`fas ${c.icon}`} style={{ fontSize: 9 }}></i> {label}
    </span>
  );
};

/** Align with Fine Tuning Job Details (Connector): succeeded = success green, not warning orange. */
const statusColorMap: Record<string, string> = {
  completed: '#0072C6',
  succeeded: '#0072C6',
  running: '#FF8300',
  pending: '#FF8300',
  queued: '#FF8300',
  failed: '#FF8300',
};

const StatusBadge: React.FC<{ value: string }> = ({ value }) => {
  const color = statusColorMap[value?.toLowerCase()] || '#5B7893';
  return (
    <span className="usage-status-badge" style={{ color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }}></span>
      {value}
    </span>
  );
};

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="usage-detail-field">
    <div className="usage-detail-field-label">{label}</div>
    <div>{children}</div>
  </div>
);

// --- Styles ---
const usageStyles = `
.usage-page { max-width: 100%; padding-bottom: calc(80px + env(safe-area-inset-bottom)); box-sizing: border-box; }

.usage-header {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 12px; margin-bottom: 20px;
}
.usage-title { margin: 0; font-size: 20px; font-weight: 700; color: #0A2A47; display: flex; align-items: center; }
.usage-refresh-btn {
  padding: 8px 16px; border-radius: 8px; border: 1px solid #C6E0F7; background: #FFFFFF;
  color: #4A6683; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 6px; transition: all 0.2s;
}
.usage-refresh-btn:hover { background: #F5F9FE; }
.usage-refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Totals */
.usage-totals-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px; margin-bottom: 20px;
}
.usage-total-card {
  padding: 12px 16px; border-radius: 10px; background: #FFFFFF; border: 1px solid #E3F0FB;
  transition: transform 0.15s;
}
.usage-total-card:hover { transform: translateY(-1px); }
.usage-total-card--primary { background: #FFFFFF; border-color: #C6E0F7; }
.usage-total-label { font-size: 11px; color: #5B7893; margin-bottom: 3px; font-weight: 500; }
.usage-total-value { font-size: 18px; font-weight: 700; color: #0A2A47; font-variant-numeric: tabular-nums; }
.usage-total-card--primary .usage-total-value { color: #0072C6; }

/* Filters */
.usage-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
  align-items: flex-end;
}
.usage-filter-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.usage-filter-field label { font-size: 11px; font-weight: 600; color: #5B7893; }
.usage-filter-field select,
.usage-filter-field input {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 38px;
  min-height: 38px;
  max-height: 38px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #C6E0F7;
  font-size: 13px;
  line-height: 1.25;
  background: #FFFFFF;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
}
.usage-filter-field select {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235B7893' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
}
.usage-filter-field .custom-datepicker { width: 100%; min-width: 0; }
.usage-filter-field .custom-datepicker-trigger {
  width: 100%;
  min-width: 0;
  height: 38px;
  min-height: 38px;
  max-height: 38px;
  padding: 8px 12px;
  box-sizing: border-box;
}
.usage-clear-btn {
  padding: 0 14px;
  height: 38px;
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #FFD9B3;
  background: #FFFFFF;
  color: #FF8300;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s;
  align-self: flex-end;
  box-sizing: border-box;
}
.usage-clear-btn:hover { background: #E3F0FB; }

.usage-error { padding: 10px 16px; background: #FFFFFF; border: 1px solid #FFD9B3; border-radius: 8px; color: #FF8300; font-size: 13px; margin-bottom: 16px; }
.usage-loading { text-align: center; padding: 48px 16px; color: #5B7893; }
.usage-empty { text-align: center; padding: 48px 16px; color: #7F9AB3; }
.usage-empty i { font-size: 32px; margin-bottom: 12px; opacity: 0.4; }
.usage-empty h3 { font-size: 16px; color: #5B7893; margin: 0 0 6px; }
.usage-empty p { font-size: 13px; margin: 0; }
.usage-count { font-size: 12px; color: #5B7893; margin-bottom: 8px; }

/* Table */
.usage-table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid #E3F0FB; }
.usage-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.usage-table th { padding: 10px 14px; background: #FFFFFF; color: #4A6683; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #E3F0FB; white-space: nowrap; }
.usage-table td { padding: 10px 14px; border-bottom: 1px solid #F5F9FE; color: #4A6683; white-space: nowrap; }
.usage-table--compact th, .usage-table--compact td { padding: 7px 10px; font-size: 12px; }
.usage-table tbody tr:last-child td { border-bottom: none; }

/* Clickable rows */
.usage-row-clickable { cursor: pointer; transition: background 0.15s; }
.usage-row-clickable:hover { background: #FFFFFF !important; }
.usage-row-clickable:active { background: #E3F0FB !important; }
.usage-row-arrow { color: #C6E0F7; font-size: 11px; transition: all 0.2s; }
.usage-row-clickable:hover .usage-row-arrow { color: #0099FA; transform: translateX(2px); }

/* Mobile cards — hidden by default, shown on mobile */
.usage-cards { display: none; }

.usage-card {
  background: #FFFFFF; border: 1px solid #E3F0FB; border-radius: 12px; padding: 14px 16px;
  margin-bottom: 10px; transition: all 0.2s;
}
.usage-card--clickable { cursor: pointer; }
.usage-card--clickable:hover { border-color: #C6E0F7; box-shadow: 0 2px 8px rgba(0, 153, 250, 0.12); }
.usage-card--clickable:active { background: #FFFFFF; }
.usage-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.usage-card-date { font-size: 12px; color: #5B7893; margin-bottom: 10px; }
.usage-card-tokens { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 8px; }
.usage-card-token { display: flex; flex-direction: column; }
.usage-card-token-label { font-size: 10px; color: #7F9AB3; font-weight: 500; text-transform: uppercase; letter-spacing: 0.3px; }
.usage-card-token-value { font-size: 14px; font-weight: 500; color: #4A6683; font-variant-numeric: tabular-nums; }
.usage-card-token-value--bold { font-weight: 700; color: #0A2A47; }
.usage-card-hint {
  margin-top: 10px; padding-top: 8px; border-top: 1px solid #F5F9FE;
  font-size: 11px; color: #C6E0F7; font-weight: 600; display: flex; align-items: center; gap: 4px;
}

/* Badges */
.usage-func-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px;
  white-space: nowrap;
}
.usage-status-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 600;
}

/* Pagination */
.usage-pagination {
  display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 16px; margin-bottom: 32px;
}
.usage-page-btn {
  padding: 7px 16px; border-radius: 8px; border: 1px solid #C6E0F7; background: #FFFFFF;
  color: #4A6683; font-size: 12px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 5px; transition: all 0.15s;
}
.usage-page-btn:hover:not(:disabled) { background: #F5F9FE; }
.usage-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.usage-page-info { font-size: 13px; color: #5B7893; }

/* Modal — in portal over body, always on top of header/sidebar */
.usage-modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(10, 42, 71, 0.45);
  display: flex; justify-content: center; align-items: center; z-index: 99999; padding: 16px;
  animation: usageFadeIn 0.15s ease;
}
.usage-modal {
  background: #FFFFFF; border-radius: 14px; padding: 24px;
  max-width: var(--modal-width-default, min(760px, calc(100vw - 32px))); width: 100%;
  max-height: 85vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(10, 42, 71, 0.18);
  animation: usageSlideUp 0.2s ease;
}
.usage-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.usage-modal-header h3 { margin: 0; font-size: 17px; font-weight: 700; display: flex; align-items: center; }
.usage-modal-close { background: none; border: none; font-size: 18px; cursor: pointer; color: #5B7893; padding: 4px; border-radius: 6px; transition: background 0.15s; }
.usage-modal-close:hover { background: #F5F9FE; }
.usage-modal-loading { text-align: center; padding: 32px; color: #5B7893; }

.usage-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; font-size: 13px; }
.usage-detail-field-label { font-size: 11px; color: #7F9AB3; margin-bottom: 2px; font-weight: 500; }
.usage-code { font-size: 11px; background: #F5F9FE; padding: 2px 6px; border-radius: 4px; word-break: break-all; }

@keyframes usageFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes usageSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

/* Responsive */
@media (max-width: 768px) {
  .usage-table-wrap { display: none; }
  .usage-cards { display: block; }
  .usage-totals-grid { grid-template-columns: repeat(3, 1fr); }
  .usage-total-value { font-size: 15px; }
  .usage-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    align-items: stretch;
  }
  .usage-filter-field { min-width: 0; }
  .usage-filter-field select,
  .usage-filter-field input {
    font-size: 12px;
    height: 38px;
    min-height: 38px;
    max-height: 38px;
  }
  .usage-filter-field .custom-datepicker-trigger {
    height: 38px;
    min-height: 38px;
    max-height: 38px;
  }
  .usage-clear-btn { min-height: 38px; align-self: flex-end; }
  .usage-detail-grid { grid-template-columns: 1fr; }
  .usage-modal { padding: 16px; border-radius: 12px; }
}

@media (max-width: 480px) {
  .usage-totals-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .usage-total-card { padding: 10px 12px; }
  .usage-total-value { font-size: 14px; }
  .usage-header { flex-direction: column; align-items: flex-start; }
  .usage-filters {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .usage-filter-field { width: 100%; }
  .usage-clear-btn { align-self: stretch; }
  .usage-pagination { margin-bottom: 40px; }
  .usage-card-tokens { grid-template-columns: 1fr 1fr; }
}
`;

export default UsageTransactionsPage;
